import React from "react";

import {Switch} from "@headlessui/react";
import {ChevronRightIcon} from "@heroicons/react/20/solid";
import {
    AdjustmentsVerticalIcon,
    CheckCircleIcon,
    TableCellsIcon,
} from "@heroicons/react/24/outline";
import {CheckIcon} from "@heroicons/react/24/solid";
import {FormHandler} from "reactivated/dist/forms";

import * as constants from "@client/constants";
import * as typography from "@client/shared/typography";

import {ManagerLayout} from "@client/site/ManagerLayout";
import * as forms from "@client/site/forms";
import {classNames, server} from "@reactivated";

const people = [
    {id: 0, name: "Monday"},
    {id: 1, name: "Tuesday"},
    {id: 2, name: "Wednesday"},
    {id: 3, name: "Thursday"},
    {id: 4, name: "Friday"},
    {id: 5, name: "Saturday"},
    {id: 6, name: "Sunday"},
];

export const TargetForm = (props: {
    form: FormHandler<server.manager.templates.CreateTarget["form"]["fields"]>;
    type: "BASIC" | "ADVANCED" | "LOG" | null;
}) => {
    const {hidden, visible} =
        props.type == "ADVANCED"
            ? {hidden: [], visible: constants.ALL_METRICS}
            : props.type == "BASIC"
              ? {hidden: constants.OPTIONAL_METRICS, visible: constants.CORE_METRICS}
              : {hidden: constants.ALL_METRICS, visible: []};

    return (
        <>
            <div className="hidden">
                {hidden.map(([fieldName]) => (
                    <forms.Fields
                        key={fieldName}
                        form={props.form}
                        noLabel
                        fields={[`${fieldName}_target`, `${fieldName}_mode`]}
                    />
                ))}
            </div>

            <div className="flex flex-col gap-4">
                {visible.map(([fieldName, , , unit, label]) => (
                    <div key={fieldName}>
                        <forms.Label label={label} error={null} />
                        <div className="mt-2 flex items-center gap-2">
                            <forms.Fields
                                form={props.form}
                                noLabel
                                fields={[`${fieldName}_mode`]}
                            />

                            <div className="flex-1">
                                <forms.Fields
                                    form={props.form}
                                    noLabel
                                    fields={[`${fieldName}_target`]}
                                />
                            </div>
                            <div>{unit}</div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

const Schedule = (props: {
    targetName: string;
    assignments: server.manager.templates.CreateTarget["assignments"];
    scheduleForm: FormHandler<
        server.manager.templates.CreateTarget["schedule_form"]["fields"]
    >;
}) => (
    <fieldset>
        <h2 className="text-base/6 font-medium text-gray-900">
            Choose days of the week
        </h2>
        <div className="mt-4 -mb-2 flex">
            <div className="flex-1 text-sm/6 font-medium text-gray-900">All</div>
            <div>
                <input
                    type="checkbox"
                    checked={props.scheduleForm.form.iterator
                        .map((field) => props.scheduleForm.values[field])
                        .every((value) => value)}
                    onChange={(event) => {
                        props.scheduleForm.form.iterator.forEach((field) => {
                            props.scheduleForm.setValue(field, event.target.checked);
                        });
                    }}
                    className="
                        size-4 rounded-sm border-gray-300 text-indigo-600
                        focus:ring-indigo-600
                    "
                />
            </div>
        </div>
        <div className="mt-4 mb-8 divide-y divide-gray-200 border-y border-gray-200">
            {people.map((person, personIdx) => {
                const checked =
                    props.scheduleForm.values[
                        props.scheduleForm.form.iterator[personIdx]
                    ];
                const currentTarget =
                    checked === true
                        ? props.targetName
                        : props.assignments.find((a) => a.day_of_week == personIdx)
                              ?.target?.name;
                return (
                    <div key={personIdx} className="relative flex items-center py-4">
                        <div className="min-w-0 flex-1 text-sm/6">
                            <label
                                htmlFor={`person-${person.id}`}
                                className="font-medium text-gray-900 select-none"
                            >
                                {person.name}
                            </label>
                            {currentTarget != null ? (
                                <div>{currentTarget}</div>
                            ) : (
                                <div className="text-gray-500">No target assigned</div>
                            )}
                        </div>
                        <div className="ml-3 flex h-6 items-center">
                            <input
                                id={`person-${person.id}`}
                                name={
                                    props.scheduleForm.fields[
                                        props.scheduleForm.form.iterator[personIdx]
                                    ].name
                                }
                                type="checkbox"
                                checked={checked}
                                onChange={(event) =>
                                    props.scheduleForm.fields[
                                        props.scheduleForm.form.iterator[personIdx]
                                    ].handler(event.target.checked)
                                }
                                className="
                                    size-4 rounded-sm border-gray-300 text-indigo-600
                                    focus:ring-indigo-600
                                "
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    </fieldset>
);

interface Step {
    id: string;
    name: string;
    onClick: () => void;
    status: "complete" | "current" | "upcoming";
}

const Steps = ({steps}: {steps: Step[]}) => (
    <nav aria-label="Progress">
        <ol
            role="list"
            className="
                divide-y divide-gray-300 rounded-md border border-gray-300
                md:flex md:divide-y-0
            "
        >
            {steps.map((step, stepIdx) => (
                <li
                    key={step.name}
                    className="
                        relative
                        md:flex md:flex-1
                    "
                >
                    {step.status === "complete" ? (
                        <button
                            onClick={step.onClick}
                            className="group flex w-full items-center"
                        >
                            <span
                                className="
                                    flex items-center px-6 py-4 text-sm font-medium
                                "
                            >
                                <span
                                    className="
                                        flex size-10 shrink-0 items-center
                                        justify-center rounded-full bg-indigo-600
                                        group-hover:bg-indigo-800
                                    "
                                >
                                    <CheckIcon
                                        className="size-6 text-white"
                                        aria-hidden="true"
                                    />
                                </span>
                                <span className="ml-4 text-sm font-medium text-gray-900">
                                    {step.name}
                                </span>
                            </span>
                        </button>
                    ) : step.status === "current" ? (
                        <span
                            className="flex items-center px-6 py-4 text-sm font-medium"
                            aria-current="step"
                        >
                            <span
                                className="
                                    flex size-10 shrink-0 items-center justify-center
                                    rounded-full border-2 border-indigo-600
                                "
                            >
                                <span className="text-indigo-600">{step.id}</span>
                            </span>
                            <span className="ml-4 text-sm font-medium text-indigo-600">
                                {step.name}
                            </span>
                        </span>
                    ) : (
                        <span className="group flex items-center">
                            <span
                                className="
                                    flex items-center px-6 py-4 text-sm font-medium
                                "
                            >
                                <span
                                    className="
                                        flex size-10 shrink-0 items-center
                                        justify-center rounded-full border-2
                                        border-gray-300
                                        group-hover:border-gray-400
                                    "
                                >
                                    <span
                                        className="
                                            text-gray-500
                                            group-hover:text-gray-900
                                        "
                                    >
                                        {step.id}
                                    </span>
                                </span>
                                <span
                                    className="
                                        ml-4 text-sm font-medium text-gray-500
                                        group-hover:text-gray-900
                                    "
                                >
                                    {step.name}
                                </span>
                            </span>
                        </span>
                    )}

                    {stepIdx !== steps.length - 1 ? (
                        <>
                            {/* Arrow separator for lg screens and up */}
                            <div
                                className="
                                    absolute top-0 right-0 hidden h-full w-5
                                    md:block
                                "
                                aria-hidden="true"
                            >
                                <svg
                                    className="size-full text-gray-300"
                                    viewBox="0 0 22 80"
                                    fill="none"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M0 -2L20 40L0 82"
                                        vectorEffect="non-scaling-stroke"
                                        stroke="currentcolor"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        </>
                    ) : null}
                </li>
            ))}
        </ol>
    </nav>
);

interface TargetType {
    name: string;
    description: string;
    onClick: () => void;
    iconColor: string;
    icon: typeof AdjustmentsVerticalIcon;
}

const SelectTargetType = ({items}: {items: TargetType[]}) => (
    <div className="mx-auto max-w-lg">
        <h2 className="text-base/6 font-medium text-gray-900">
            Choose your target type
        </h2>
        <p className="mt-1 text-sm text-gray-500">
            Remember, you can choose different targets for different days.
        </p>
        <ul
            role="list"
            className="mt-6 divide-y divide-gray-200 border-y border-gray-200"
        >
            {items.map((item, itemIdx) => (
                <li key={itemIdx}>
                    <div
                        className="
                            group relative flex items-start space-x-4 px-4 py-6
                            hover:bg-indigo-50
                        "
                    >
                        <div className="shrink-0">
                            <span
                                className={classNames(
                                    item.iconColor,
                                    "inline-flex h-10 w-10 items-center justify-center rounded-lg",
                                )}
                            >
                                <item.icon
                                    className="size-6 text-white"
                                    aria-hidden="true"
                                />
                            </span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                                <button
                                    className="text-left"
                                    type="button"
                                    onClick={item.onClick}
                                >
                                    <span
                                        className="absolute inset-0"
                                        aria-hidden="true"
                                    />
                                    {item.name}
                                </button>
                            </div>
                            <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                        <div className="shrink-0 self-center">
                            <ChevronRightIcon
                                className="
                                    size-5 text-gray-400
                                    group-hover:text-gray-500
                                "
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    </div>
);

export const Template = (props: server.manager.templates.CreateTarget) => {
    const title = "Create target";
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const targetForm = forms.useForm({form: props.form});
    const scheduleForm = forms.useForm({form: props.schedule_form});
    const [step, setStep] = React.useState(0);
    const [useMealPlan, setUseMealPlan] = React.useState(false);

    const items = [
        {
            name: "Just log food",
            description: "Your client just needs to log their food.",
            onClick: () => {
                targetForm.setValue("type", "LOG");
                targetForm.setValue("name", "Just log food");
                setStep(1);
            },
            iconColor: "bg-pink-500",
            icon: CheckCircleIcon,
        },
        {
            name: "Macros",
            description: "Your client needs to hit specific macros.",
            onClick: () => {
                targetForm.setValue("type", "BASIC");
                setStep(1);
            },
            iconColor: "bg-purple-500",
            icon: TableCellsIcon,
        },
        {
            name: "Advanced",
            description: "Your client needs macros and micronutrients.",
            onClick: () => {
                targetForm.setValue("type", "ADVANCED");
                setStep(1);
            },
            iconColor: "bg-yellow-500",
            icon: AdjustmentsVerticalIcon,
        },
    ] satisfies TargetType[];

    const steps = [
        {
            id: "01",
            name: "Type",
            onClick: () => setStep(0),
            status: step >= 1 ? "complete" : "current",
        },
        {
            id: "02",
            name: "Details",
            onClick: () => setStep(1),
            status: step == 0 ? "upcoming" : step == 1 ? "current" : "complete",
        },
        {
            id: "03",
            name: "Schedule",
            onClick: () => setStep(2),
            status: step != 2 ? "upcoming" : "current",
        },
    ] satisfies Step[];

    const nextMealPlan = props.meal_plans.find(
        (m) => m.id.toString() == targetForm.values.meal_plan,
    );

    const fillMacrosFromMealPlan = () => {
        if (nextMealPlan != null) {
            targetForm.setValues({
                ...targetForm.values,
                // name: nextMealPlan.name,
                calories_target:
                    nextMealPlan.calories != null
                        ? parseInt(nextMealPlan.calories).toString()
                        : "",
                protein_target:
                    nextMealPlan.protein != null
                        ? parseInt(nextMealPlan.protein).toString()
                        : "",
                fat_target:
                    nextMealPlan.fat != null
                        ? parseInt(nextMealPlan.fat).toString()
                        : "",
                carbs_target:
                    nextMealPlan.carbs != null
                        ? parseInt(nextMealPlan.carbs).toString()
                        : "",
            });
        }
    };

    const handleSubmit = () => {
        setIsSubmitting(true);
    };
    const action = isSubmitting === false ? "Create" : "Processing...";

    return (
        <ManagerLayout title={title} currentClientManagerId={props.client_manager.id}>
            <div className="mb-8">
                <Steps steps={steps} />
            </div>

            <form
                className="
                    mx-auto w-full max-w-xl bg-white p-10 shadow-xs ring-1
                    ring-gray-900/5
                    sm:rounded-xl
                "
                onSubmit={handleSubmit}
                noValidate
                method="POST"
                action=""
            >
                <forms.CSRFToken />
                <input
                    type="hidden"
                    name={targetForm.fields.type.name}
                    value={targetForm.fields.type.value ?? ""}
                />
                <div
                    className={classNames("my-8 flex flex-col", step != 0 && "hidden")}
                >
                    {/*
                    <forms.Fields form={targetForm} fields={["type"]} />
                    */}
                    <SelectTargetType items={items} />
                </div>
                <div
                    className={classNames(
                        "my-8 flex flex-col gap-4",
                        step != 1 && "hidden",
                    )}
                >
                    <Switch.Group
                        as="div"
                        className="flex items-center justify-between"
                    >
                        <span className="flex grow flex-col">
                            <Switch.Label
                                as="span"
                                className="text-sm/6 font-medium text-gray-900"
                                passive
                            >
                                Use a meal plan
                            </Switch.Label>
                            <Switch.Description
                                as="span"
                                className="text-sm text-gray-500"
                            >
                                Meal plans let you prescribe specific foods or
                                guidelines.
                            </Switch.Description>
                        </span>
                        <Switch
                            checked={useMealPlan}
                            onChange={setUseMealPlan}
                            className={classNames(
                                useMealPlan ? "bg-indigo-600" : "bg-gray-200",
                                "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                            )}
                        >
                            <span
                                aria-hidden="true"
                                className={classNames(
                                    useMealPlan ? "translate-x-5" : "translate-x-0",
                                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out",
                                )}
                            />
                        </Switch>
                    </Switch.Group>
                    {useMealPlan ? (
                        <div
                            className="
                                flex flex-col gap-2
                                sm:flex-row
                            "
                        >
                            <div className="flex-1">
                                <forms.Fields
                                    form={targetForm}
                                    fields={["meal_plan"]}
                                />
                            </div>
                            <div
                                className={classNames(
                                    "mb-1",
                                    (targetForm.values.type == "LOG" ||
                                        targetForm.values.meal_plan == "" ||
                                        targetForm.values.meal_plan == null) &&
                                        "invisible",
                                    targetForm.errors?.meal_plan != null
                                        ? "mt-2 sm:self-center"
                                        : "sm:self-end",
                                )}
                            >
                                <button
                                    type="button"
                                    onClick={fillMacrosFromMealPlan}
                                    className={classNames(
                                        "rounded-sm bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
                                    )}
                                >
                                    Use meal plan macros
                                </button>
                            </div>
                        </div>
                    ) : (
                        <forms.Fields form={targetForm} fields={["name"]} />
                    )}
                    <div className={classNames("flex flex-col gap-2 max-w-lg")}>
                        <TargetForm type={targetForm.values.type} form={targetForm} />
                    </div>
                    <typography.Button
                        onClick={() => {
                            if (
                                useMealPlan == false &&
                                (targetForm.values.name == "" ||
                                    targetForm.values.name == null)
                            ) {
                                targetForm.setErrors({
                                    name: ["A target name is required"],
                                });
                            } else if (
                                useMealPlan == true &&
                                (targetForm.values.meal_plan == "" ||
                                    targetForm.values.meal_plan == null)
                            ) {
                                targetForm.setErrors({
                                    meal_plan: ["A meal plan is required"],
                                });
                            } else {
                                setStep(2);
                            }
                        }}
                    >
                        Next
                    </typography.Button>
                </div>
                <div className={classNames(step != 2 && "hidden")}>
                    <Schedule
                        targetName={nextMealPlan?.name ?? targetForm.values.name ?? ""}
                        assignments={props.assignments}
                        scheduleForm={scheduleForm}
                    />
                    <typography.Button submit disabled={isSubmitting}>
                        {action}
                    </typography.Button>
                </div>
            </form>
        </ManagerLayout>
    );
};
